import { LoadSingleTexture } from './LoadSingleTexture';
import { ResourceType } from './ResourceType';

// LOAD TEXTURES AND SPRITES

// LOADING PROGRESS
function _addProgressTracking(progressTracker, loader, doThat) {
    return new Promise((resolve) => {
        if (!doThat) resolve(null);

        loader.onProgress = progressTracker;
        resolve(doThat);
    });
}

function _countItem(loader, doThat) {
    return new Promise((resolve) => {
        if (!doThat) resolve(null);

        loader.loaded += 1;
        if (loader.total > 0) loader.progress = 0.01 + 0.99 * (loader.loaded / loader.total);
        if (typeof loader.onProgress === 'function') loader.onProgress(loader.progress);

        resolve(doThat);
    });
}

function _removeAllProgressTracking(loader, doThat) {
    return new Promise((resolve) => {
        if (!doThat) resolve(null);
        loader.onProgress = null;

        resolve(doThat);
    });
}
/* ----- */

// LOADING
function _setupSingleUrlLoading(loader, input) {
    loader.total = 1;

    return [{ url: input }];
}

function _setupSingleItemLoading(loader, input) {
    loader.total = 1;

    return [input];
}

function _setupItemsArrayLoading(loader, input) {
    loader.total = input.length;

    return input.concat([]);
}

function _setupLoading(loader, input) {
    return new Promise((resolve) => {
        if (!input || (typeof input !== 'string' && !(input instanceof Object))) resolve(null);

        loader.progress = 0.01;
        loader.loaded = 0;
        loader.resources = {};

        //
        // 1. Load items array:
        loader.order = input;

        if (typeof input === 'string') {
            //
            // 2. Load single file with no type:

            loader.order = _setupSingleUrlLoading(loader, input);
            //
        } else if (input instanceof Array) {
            loader.order = _setupItemsArrayLoading(loader, input);
        } else {
            //
            // 3. Load single item:

            if (input.type && input.type === ResourceType.MANIFEST) {
                //
                // 3.1. Manifest

                LoadSingleTexture(input.id || input.name, input.url, true, true, input.type, false)
                    .then((data) => {
                        loader.order = _setupItemsArrayLoading(loader, data);
                        resolve(loader.resources);
                    })
                    .catch((/* err */) => {
                        // console.log(err);
                        throw new Error(`Resource ${input.url}: no correct manifest has found`);
                    });

                return;

                //
            }
            //
            // 3.1. Data

            loader.order = _setupSingleItemLoading(loader, input);
            //
        }

        resolve(loader.order);
    });
}

function _loadItem(item, store, storage, doThat) {
    return new Promise((resolve) => {
        if (!doThat || !item) resolve(doThat);

        LoadSingleTexture(item.id || item.name, item.url, true, doThat, store, storage)
            .then((texture) => {
                const result = {
                    id: item.id || item.name,
                    url: item.url,
                    ok: true,
                    texture
                };

                if (typeof doThat === 'object') {
                    doThat[result.id || result.url] = result;
                }
                resolve(doThat);
            })
            .catch((/* err */) => {
                /* console.log(err); */
                const result = {
                    id: item.id || item.name,
                    url: item.url,
                    ok: false,
                    error: true,
                    texture: null
                };

                if (typeof doThat === 'object') {
                    doThat[result.id || result.url] = result;
                }
                resolve(doThat);
            });
    });
}

function _load(loader, store, storage, doThat) {
    return new Promise((resolve) => {
        if (!doThat) resolve(null);
        if (loader.order.length < 1) resolve(doThat);

        const __loadNextItem = (item, storeIn, storageIn) => (doThatIn) => _loadItem(item, storeIn, storageIn, doThatIn);

        const __countItem = (doThatIn) => _countItem(loader, doThatIn);

        let promise = _loadItem(loader.order.shift(), store, storage, doThat).then(__countItem);

        while (loader.order.length > 0) {
            promise = promise.then(__loadNextItem(loader.order.shift(), store, storage)).then(__countItem);
        }

        promise
            .then((doThatIn) => {
                resolve(doThatIn);
            })
            .catch(() => {
                resolve(doThat);
            });
    });
}

/* ------- */

// LOADING RESULT CHECKING
function _checkResources(resources, critical) {
    return new Promise((resolve, reject) => {
        if (!resources) {
            resolve(null);

            return;
        }

        if (critical) {
            const keys = Object.keys(resources);

            for (let i = 0; i < keys.length; i += 1) {
                if (resources[keys[i]].error) {
                    reject();

                    return;
                }
            }

            resolve(resources);

            return;
        }
        const keys = Object.keys(resources);

        for (let i = 0; i < keys.length; i += 1) {
            if (!resources[keys[i]].error) {
                resolve(resources);

                return;
            }
        }

        resolve(null);
    });
}
/* ----- */

function LoadTextures(manifest, critical, doThat, progressUpdate = false, store = true, storage = null) {
    return new Promise((resolve, reject) => {
        if (!doThat) {
            resolve(doThat);

            return;
        }

        const loader = {};

        const __setupLoader = ((ldr) => (input) => _setupLoading(ldr, input))(loader);

        const __addProgressTracking = (ldr, doThatIn) => _addProgressTracking(progressUpdate, ldr, doThatIn);

        const __trackProgress = ((ldr) => (doThatIn) => __addProgressTracking(ldr, doThatIn))(loader);

        const __stopAllTrackProgress = ((ldr) => (doThatIn) => _removeAllProgressTracking(ldr, doThatIn))(loader);

        const __load = ((ldr, storeIn) => (doThatIn) => _load(loader, storeIn, ldr, doThatIn))(loader, store, storage);

        const __checkResources = ((criticalIn) => (doThatIn) => _checkResources(doThatIn, criticalIn))(critical);

        __setupLoader(manifest)
            .then(__trackProgress)
            .then(__load)
            .then(__stopAllTrackProgress)
            .then(__checkResources)
            .then((resources) => {
                if (resources) resolve(resources);
                else if (critical) {
                    reject();

                    return;
                }
                resolve(null);
            })
            .catch((err) => {
                console.log(err);
                if (critical) {
                    reject();

                    return;
                }
                resolve(null);
            });
    });
}

const LoadTexturesFunction = (
    manifest, critical, progressUpdate, store, storage
) => (doThat) => LoadTextures(manifest, critical, doThat, progressUpdate, store, storage);

export { LoadTextures, LoadTexturesFunction };
export default LoadTextures;
